import { useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Download,
  Loader2,
  Bot,
  User,
  Calendar,
  Mail,
  MessageSquare,
  Clock,
  FileText,
  FileJson,
  ChevronDown,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { PageHeader } from '@/components/PageHeader';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ChatMessageLog } from '@/core/models';

export const Route = createFileRoute('/conversation/$conversationId')({
  head: () => ({
    meta: [{ title: 'Dettaglio conversazione · Replya Internal Backoffice' }],
  }),
  component: ConversationDetailPage,
});

function formatDateTime(iso?: string) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MessageBubble({ msg }: { msg: ChatMessageLog }) {
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isUser
            ? 'bg-brand text-brand-foreground'
            : isSystem
              ? 'bg-muted text-muted-foreground'
              : 'bg-surface-3 text-foreground'
        }`}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      <div className={`max-w-[75%] space-y-1 ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div
          className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
            isUser
              ? 'bg-brand text-brand-foreground rounded-br-none'
              : isSystem
                ? 'bg-muted text-muted-foreground rounded-bl-none'
                : 'bg-surface-2 border border-border rounded-bl-none'
          }`}
        >
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="m-0">{children}</p>,
              }}
            >
              {msg.content}
            </ReactMarkdown>
          </div>
        </div>
        <span className="text-[10px] text-muted-foreground px-1">
          {formatTime(msg.createdAt)}
        </span>
      </div>
    </div>
  );
}

function ConversationDetailPage() {
  const { conversationId } = Route.useParams();
  const { selectedTenant } = useAppState();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: detail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['conversation-detail', selectedTenant?.tenantId, conversationId],
    queryFn: () => apiService.getConversationDetail(selectedTenant!.tenantId, conversationId),
    enabled: !!selectedTenant,
  });

  const { data: messages, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['conversation-messages', selectedTenant?.tenantId, conversationId],
    queryFn: () =>
      apiService.getChatMessageLogs(selectedTenant!.tenantId, {
        conversationId,
        limit: 1000,
      }),
    enabled: !!selectedTenant,
  });

  const conversation = useMemo(() => {
    const raw = detail ?? {};
    const conv = (raw.conversation as Record<string, unknown> | undefined) ?? raw;
    return {
      conversationId: String(conv.conversationId ?? conversationId),
      firebaseId: conv.firebaseId ? String(conv.firebaseId) : undefined,
      userEmail: conv.userEmail ? String(conv.userEmail) : undefined,
      userDisplayName: conv.userDisplayName ? String(conv.userDisplayName) : undefined,
      firstMessageAt: conv.firstMessageAt ? String(conv.firstMessageAt) : undefined,
      lastMessageAt: conv.lastMessageAt ? String(conv.lastMessageAt) : undefined,
      messageCount: Number(conv.messageCount ?? 0),
      summary: conv.summary ? String(conv.summary) : undefined,
    };
  }, [detail, conversationId]);

  const sortedMessages = useMemo(() => {
    if (!messages?.items) return [];
    return [...messages.items].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages?.items]);

  const triggerDownload = (payload: string, filename: string, type: string) => {
    const blob = new Blob([payload], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportTxt = () => {
    if (!sortedMessages.length) return;
    const lines = [
      `Conversazione: ${conversation.conversationId}`,
      `Tenant: ${selectedTenant!.tenantId}`,
      `Utente: ${conversation.userDisplayName || conversation.userEmail || conversation.firebaseId || 'anonimo'}`,
      `Messaggi: ${sortedMessages.length}`,
      `Dal: ${formatDateTime(conversation.firstMessageAt)}`,
      `Al: ${formatDateTime(conversation.lastMessageAt)}`,
      '---',
      ...sortedMessages.map((msg) => {
        const when = formatDateTime(msg.createdAt);
        const who = msg.role === 'assistant' ? 'Assistente' : msg.role === 'system' ? 'Sistema' : 'Utente';
        return `[${when}] ${who}:\n${msg.content}`;
      }),
    ];
    triggerDownload(lines.join('\n\n'), `conversazione-${conversation.conversationId}.txt`, 'text/plain;charset=utf-8');
  };

  const handleExportMarkdown = () => {
    if (!sortedMessages.length) return;
    const userLabel = conversation.userDisplayName || conversation.userEmail || conversation.firebaseId || 'anonimo';
    const lines = [
      `# Conversazione ${conversation.conversationId}`,
      '',
      `- **Tenant:** ${selectedTenant!.tenantId}`,
      `- **Utente:** ${userLabel}`,
      `- **Messaggi:** ${sortedMessages.length}`,
      `- **Inizio:** ${formatDateTime(conversation.firstMessageAt)}`,
      `- **Fine:** ${formatDateTime(conversation.lastMessageAt)}`,
      '',
      '---',
      '',
      '## Messaggi',
      '',
      ...sortedMessages.map((msg) => {
        const when = formatDateTime(msg.createdAt);
        const who = msg.role === 'assistant' ? 'Assistente' : msg.role === 'system' ? 'Sistema' : 'Utente';
        return [`### ${when} · ${who}`, '', msg.content, ''].join('\n');
      }),
    ];
    triggerDownload(lines.join('\n'), `conversazione-${conversation.conversationId}.md`, 'text/markdown;charset=utf-8');
  };

  const handleExportJson = () => {
    if (!sortedMessages.length) return;
    const payload = {
      conversationId: conversation.conversationId,
      tenantId: selectedTenant!.tenantId,
      user: {
        firebaseId: conversation.firebaseId,
        email: conversation.userEmail,
        displayName: conversation.userDisplayName,
      },
      firstMessageAt: conversation.firstMessageAt,
      lastMessageAt: conversation.lastMessageAt,
      messageCount: sortedMessages.length,
      messages: sortedMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
        createdAt: msg.createdAt,
      })),
    };
    triggerDownload(JSON.stringify(payload, null, 2), `conversazione-${conversation.conversationId}.json`, 'application/json');
  };

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  const isLoading = isLoadingDetail || isLoadingMessages;

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader title="Dettaglio conversazione" subtitle="Audit dei messaggi scambiati con il chatbot">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-xl" disabled={!sortedMessages.length}>
                <Download className="mr-2 h-4 w-4" />
                Esporta
                <ChevronDown className="ml-2 h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={handleExportMarkdown}>
                <FileText className="mr-2 h-4 w-4" />
                Markdown
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportTxt}>
                <FileText className="mr-2 h-4 w-4" />
                Testo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJson}>
                <FileJson className="mr-2 h-4 w-4" />
                JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button asChild variant="outline" size="sm" className="rounded-xl">
            <Link to="/conversations">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Indietro
            </Link>
          </Button>
        </div>
      </PageHeader>

      <Card className="rounded-2xl border bg-surface overflow-hidden">
        <CardHeader className="border-b bg-surface-2/50 px-5 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span className="font-mono">{conversation.conversationId}</span>
              </div>
              {conversation.summary && (
                <p className="text-sm font-medium">{conversation.summary}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="rounded-lg">
                <MessageSquare className="mr-1.5 h-3 w-3" />
                {sortedMessages.length} messaggi
              </Badge>
              <Badge variant="outline" className="rounded-lg">
                <Clock className="mr-1.5 h-3 w-3" />
                {formatDateTime(conversation.lastMessageAt)}
              </Badge>
            </div>
          </div>

          <div className="border-t my-4" />

          <div className="grid gap-3 md:grid-cols-3">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Inizio</p>
                <p className="font-medium">{formatDateTime(conversation.firstMessageAt)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Fine</p>
                <p className="font-medium">{formatDateTime(conversation.lastMessageAt)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Utente</p>
                <p className="font-medium truncate">
                  {conversation.userDisplayName || conversation.userEmail || conversation.firebaseId || 'anonimo'}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Caricamento conversazione...
            </div>
          ) : sortedMessages.length === 0 ? (
            <p className="text-center text-muted-foreground py-16">
              Nessun messaggio trovato per questa conversazione.
            </p>
          ) : (
            <div
              ref={scrollRef}
              className="space-y-6 p-5 max-h-[calc(100vh-20rem)] overflow-y-auto"
            >
              {sortedMessages.map((msg, idx) => (
                <MessageBubble key={msg.id || `msg-${idx}`} msg={msg} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
